import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { sendChatMessage } from "@/services/chat-service";

// Keep the schema/key consumed by the existing readiness checklist.
export const STORAGE_KEY = "chat_session";
const messageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  timestamp: z.string(),
});
const sessionSchema = z.object({
  id: z.string().default(() => crypto.randomUUID()),
  ownerId: z.string().optional(),
  projectName: z.string().default("User님의 프로젝트"),
  messages: z.array(messageSchema),
  currentStep: z.number().int().min(0).max(20),
  totalSteps: z.number().int().min(0),
  stepHistory: z
    .array(
      z.object({ role: z.enum(["user", "assistant"]), content: z.string() }),
    )
    .default([]),
  isCompleted: z.boolean().default(false),
  // Retain context only for sessions saved before the selection UI was removed.
  // New conversations omit both fields and use the backend's default questions.
  sector: z.string().nullable().optional(),
  valueChain: z.string().nullable().optional(),
});
const messageResponseSchema = z.object({
  reply: z.string().min(1),
  current_step: z.number().int().min(0).max(20),
  total_steps: z.number().int().positive(),
  is_completed: z.boolean(),
});
type Session = z.infer<typeof sessionSchema>;
const emptySession = (ownerId?: string): Session => ({
  id: crypto.randomUUID(),
  ownerId,
  projectName: "User님의 프로젝트",
  messages: [],
  currentStep: 0,
  totalSteps: 0,
  stepHistory: [],
  isCompleted: false,
});

function readSession(ownerId?: string): Session {
  try {
    const parsed = sessionSchema.safeParse(
      JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null"),
    );
    if (
      parsed.success &&
      (!parsed.data.ownerId || parsed.data.ownerId === ownerId) &&
      parsed.data.messages.some((message) => message.role === "user")
    ) {
      return { ...parsed.data, ownerId };
    }
  } catch {
    /* Invalid or unavailable storage must not break the page. */
  }
  return emptySession(ownerId);
}

export function useConsultation(ownerId?: string) {
  const [session, setSession] = useState(() => readSession(ownerId));
  const sessionRef = useRef(session);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const resumeAllowedRef = useRef(true);
  const mountedRef = useRef(false);

  const persist = useCallback((next: Session) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setStorageError(null);
      return true;
    } catch {
      setStorageError(
        "상담을 임시 저장하지 못했습니다. 브라우저 저장 공간과 설정을 확인해 주세요. 화면을 나가면 대화가 사라질 수 있습니다.",
      );
      return false;
    }
  }, []);

  const commit = useCallback(
    (next: Session) => {
      sessionRef.current = next;
      setSession(next);
      persist(next);
    },
    [persist],
  );

  const requestReply = useCallback(
    async (snapshot: Session) => {
      const last = snapshot.messages[snapshot.messages.length - 1];
      if (
        requestRef.current ||
        snapshot.isCompleted ||
        last?.role !== "user"
      )
        return;
      const controller = new AbortController();
      requestRef.current = controller;
      setIsLoading(true);
      setError(null);
      try {
        const response = await sendChatMessage(
          {
            message: last.content,
            current_step: snapshot.currentStep,
            step_history: snapshot.stepHistory.length
              ? snapshot.stepHistory.slice(-20)
              : null,
            sector: snapshot.sector,
            value_chain: snapshot.valueChain,
          },
          {
            signal: controller.signal,
            timeout: 120_000,
            skipServerErrorRedirect: true,
          },
        );
        if (
          controller.signal.aborted ||
          !mountedRef.current ||
          sessionRef.current.id !== snapshot.id
        )
          return;
        if (!response.data.success) throw new Error("Chat request failed");
        const data = messageResponseSchema.parse(response.data.data);
        const stepHistory: Session["stepHistory"] =
          data.current_step !== snapshot.currentStep
            ? []
            : [
                ...snapshot.stepHistory,
                { role: "user", content: last.content },
                { role: "assistant", content: data.reply },
              ];
        commit({
          ...sessionRef.current,
          messages: [
            ...snapshot.messages,
            {
              id: crypto.randomUUID(),
              role: "assistant",
              content: data.reply,
              timestamp: new Date().toISOString(),
            },
          ],
          currentStep: data.current_step,
          totalSteps: data.total_steps,
          isCompleted: data.is_completed,
          stepHistory: stepHistory.slice(-20),
        });
        resumeAllowedRef.current = true;
      } catch (failure) {
        if (
          controller.signal.aborted ||
          !mountedRef.current ||
          sessionRef.current.id !== snapshot.id
        )
          return;
        resumeAllowedRef.current = false;
        if (!axios.isCancel(failure)) setError("답변을 불러오지 못했어요.");
      } finally {
        if (requestRef.current === controller) {
          requestRef.current = null;
          if (mountedRef.current) setIsLoading(false);
        }
      }
    },
    [commit],
  );

  useEffect(() => {
    mountedRef.current = true;
    void requestReply(sessionRef.current);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        const pending =
          sessionRef.current.messages[sessionRef.current.messages.length - 1];
        if (pending?.role === "user") {
          requestRef.current?.abort();
          requestRef.current = null;
          setIsLoading(false);
        }
      } else if (resumeAllowedRef.current) {
        void requestReply(sessionRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility);
    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [requestReply]);

  return {
    session,
    isLoading,
    error,
    storageError,
    isStarted: session.messages.length > 0,
    send: (content: string) => {
      const current = sessionRef.current;
      const pending = current.messages[current.messages.length - 1];
      if (
        requestRef.current ||
        current.isCompleted ||
        pending?.role === "user" ||
        !content.trim() ||
        content.trim().length > 5000
      )
        return false;
      const next: Session = {
        ...current,
        messages: [
          ...current.messages,
          {
            id: crypto.randomUUID(),
            role: "user",
            content: content.trim(),
            timestamp: new Date().toISOString(),
          },
        ],
      };
      commit(next);
      void requestReply(next);
      return true;
    },
    retry: () => void requestReply(sessionRef.current),
    rename: (name: string) =>
      commit({ ...sessionRef.current, projectName: name }),
    save: () => persist(sessionRef.current),
    discard: () => {
      requestRef.current?.abort();
      requestRef.current = null;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* Storage may be blocked. */
      }
    },
  };
}
